/**
 * @fileoverview Shared "recently launched applications" queue. Backs the two
 * application spots in the Shell rail AND the Applications section on the home
 * page so they always agree. Most-recently launched is first; the queue is
 * capped at MAX_RECENT_APPS.
 *
 * Storage: localStorage key `ic.recentApps` (JSON array of catalog ids).
 * Cross-component sync (rail ↔ dashboard, same tab) rides a custom
 * `iron:recent-apps-changed` event; cross-tab sync rides the native
 * `storage` event. When the user hasn't launched anything yet, the queue is
 * padded with the first live catalog entries so the slots are never empty.
 */

import { useCallback, useEffect, useState } from "react";
import { IRONCASE_APP_CATALOG, type AppCatalogEntry } from "../components/application-portal/app-catalog";

const STORAGE_KEY = "ic.recentApps";
const CHANGE_EVENT = "iron:recent-apps-changed";
export const MAX_RECENT_APPS = 2;

/** First live catalog apps — used to pad the queue before any launch. */
const DEFAULT_IDS: readonly string[] = IRONCASE_APP_CATALOG.filter((a) => a.status === "live")
  .slice(0, MAX_RECENT_APPS)
  .map((a) => a.id);

function readIds(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/** Resolve stored ids → catalog entries, dedupe, pad with defaults, cap. */
function resolve(ids: readonly string[]): AppCatalogEntry[] {
  const byId = new Map(IRONCASE_APP_CATALOG.map((a) => [a.id, a] as const));
  const out: AppCatalogEntry[] = [];
  const seen = new Set<string>();
  for (const id of [...ids, ...DEFAULT_IDS]) {
    if (seen.has(id)) continue;
    const entry = byId.get(id);
    // Live only. These ids come from localStorage, so they outlive the catalogue
    // that produced them: an app launched while it was marked live would keep
    // its rail slot after being reclassified, and the rail — unlike the portal —
    // has no "coming soon" state to render. It would just navigate to a path
    // with no route and land the user on the dashboard with no explanation.
    if (entry && entry.status === "live") {
      out.push(entry);
      seen.add(id);
    }
    if (out.length >= MAX_RECENT_APPS) break;
  }
  return out;
}

export function useRecentApps(): {
  recentApps: AppCatalogEntry[];
  recordLaunch: (appId: string) => void;
} {
  const [ids, setIds] = useState<string[]>(() => [...DEFAULT_IDS]);

  useEffect(() => {
    const sync = () => setIds(readIds());
    sync(); // hydrate from storage (SSR-safe: only runs client-side)
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CHANGE_EVENT, sync);
    };
  }, []);

  const recordLaunch = useCallback((appId: string) => {
    const next = [appId, ...readIds().filter((x) => x !== appId)].slice(0, MAX_RECENT_APPS);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable — in-memory state still updates below
    }
    setIds(next);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }, []);

  return { recentApps: resolve(ids), recordLaunch };
}
